// src/components/landing/HomeSearch.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation"; // Import useRouter for programmatic navigation
import Link from "next/link";
import Image from "next/image";

interface MediaResult {
  id: number;
  image: string;
  name: string;
  web: string;
  price: string;
  country: string | null;
}

const HomeSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MediaResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showWrapper, setShowWrapper] = useState(false);

  const router = useRouter(); // Initialize router for navigation
  const inputRef = useRef<HTMLInputElement>(null);
  const spinnerRef = useRef<HTMLSpanElement>(null);
  const resultsWrapperRef = useRef<HTMLDivElement>(null);

  const adjustWrapperWidth = () => {
    if (resultsWrapperRef.current && inputRef.current) {
      if (window.innerWidth <= 575) {
        const searchContainer = inputRef.current.closest('.home-media-search') as HTMLElement;
        if (searchContainer) {
          resultsWrapperRef.current.style.width = `${searchContainer.offsetWidth}px`;
          resultsWrapperRef.current.style.overflowX = 'hidden';
          resultsWrapperRef.current.style.maxWidth = '100%';
          const tableElement = resultsWrapperRef.current.querySelector('table');
          if (tableElement) {
            tableElement.style.width = '100%';
            tableElement.style.tableLayout = 'fixed';
          }
        }
      } else {
        resultsWrapperRef.current.style.width = `${inputRef.current.offsetWidth}px`;
      }
    }
  };

  useEffect(() => {
    adjustWrapperWidth();
    window.addEventListener("resize", adjustWrapperWidth);
    return () => window.removeEventListener("resize", adjustWrapperWidth);
  }, []);

  useEffect(() => {
    if (showWrapper) {
      adjustWrapperWidth();
    }
  }, [showWrapper, results]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsWrapperRef.current &&
        inputRef.current &&
        !resultsWrapperRef.current.contains(event.target as Node) &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowWrapper(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleFocus = () => {
    if (query.trim().length > 0 && results.length > 0) {
      setShowWrapper(true);
    }
  };

  useEffect(() => {
    if (query.trim().length < 1) {
      setResults([]);
      setShowWrapper(false);
      return;
    }

    const fetchResults = async () => {
      setLoading(true);
      setShowWrapper(true);
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/home-search?query=${encodeURIComponent(query)}`
        );
        const data = await res.json();

        if (Array.isArray(data.results)) {
          const cleanResults = data.results.map((media: MediaResult) => {
            const raw = media.image ? media.image.toString().trim() : "";
            const cleanedImage = raw.replace(/[\s\n\r]+$/, "");
            const lower = cleanedImage.toLowerCase();
            const finalImage =
              !cleanedImage ||
              cleanedImage === "/images/media-logos/" ||
              lower === "none" ||
              lower === "null"
                ? "/images/media-logos/media_default_img-2.webp"
                : cleanedImage;
            return {
              id: media.id,
              image: finalImage,
              name: media.name,
              web: media.web,
              price: media.price,
              country: media.country, // Add country field
            };
          });
          setResults(cleanResults);
          if (cleanResults.length === 0) {
            setShowWrapper(false);
          }
        } else {
          console.warn("API response does not include a results array", data);
          setResults([]);
          setShowWrapper(false);
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
        setResults([]);
        setShowWrapper(false);
      }
      setLoading(false);
    };

    const delay = setTimeout(fetchResults, 300);
    return () => clearTimeout(delay);
  }, [query]);

  // Handle row click to redirect
  const handleRowClick = (mediaName: string, mediaId: number) => {
    router.push(`/marketplace?search=${encodeURIComponent(mediaName)}&id=${mediaId}`);
  };

  return (
    <div className="home-search-block">
      <div className="home-media-search">
        <div className="hms-container">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search for media..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleFocus}
          />
          <span
            ref={spinnerRef}
            className="hm-md-loader"
            style={{ display: loading && query.trim().length > 0 ? "block" : "none" }}
          ></span>
        </div>

        <Link href="/marketplace" className="get-start-btn home-search">
          View Offers <i className="fa fa-angle-right ml-1" aria-hidden="true"></i>
        </Link>

        {showWrapper && (
          <div ref={resultsWrapperRef} className="home-media-results-wrapper">
            {results.length > 0 ? (
            <table className="home-media-results">
              <tbody>
                {results.map((media, index) => (
                  <tr
                    key={index}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('a')) return;
                      handleRowClick(media.name, media.id);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <Link href={`/marketplace?search=${encodeURIComponent(media.name)}&id=${media.id}`}>
                        <Image
                          src={media.image}
                          alt={media.name}
                          width={24}
                          height={24}
                        />
                      </Link>
                    </td>
                    <td>
                      <Link href={`/marketplace?search=${encodeURIComponent(media.name)}&id=${media.id}`}>
                        <span id="name">
                          {media.name}
                          {media.country && (
                            <Image
                              src={`/images/flags/${
                                media.country
                                  ? media.country.toLowerCase().replace(/\s+/g, "-")
                                  : "global"
                              }.svg`}
                              alt={media.country ? `${media.country} flag` : "Global flag"}
                              width={16}
                              height={16}
                              style={{ marginLeft: "5px", verticalAlign: "middle" }}
                            />
                          )}
                        </span>
                        <span id="web">{media.web}</span>
                      </Link>
                    </td>
                    <td>
                      <Link href={`/marketplace?search=${encodeURIComponent(media.name)}&id=${media.id}`}>
                        <span id="price">{media.price}</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            ) : (
              !loading &&
              query.trim().length > 0 && <p className="hm-noresults">no results found</p>
            )}
          </div>
        )}
      </div>
      <div className="search-tags-container">
        <div className="hm-tags">
          <span id="txt">Popular:</span>
          <span id="tg" onClick={() => setQuery("Forbes")}>Forbes</span>
          <span id="tg" onClick={() => setQuery("Time")}>Time</span>
          <span id="tg" onClick={() => setQuery("NY")}>NY</span>
        </div>
      </div>
    </div>
  );
};

export default HomeSearch;